import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, useLanguage } from "@/context/AppProviders";
import { LogOut, LayoutGrid, Shield, User, Menu, X, Home, BookOpen, MessageSquareQuote } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const close = () => setOpen(false);

  const linkCls = "flex items-center gap-3 px-6 py-4 text-base text-ink/80 hover:text-ember hover:bg-ember/5 transition-colors border-b border-ink/5";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-paper/75 backdrop-blur-xl border-b border-ink/10" data-testid="main-nav">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" onClick={close} className="font-display font-semibold text-2xl md:text-3xl text-ink tracking-tight">
          M.SAH<span className="text-ember">.</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10 text-sm text-ink/60">
          <Link to="/" data-testid="nav-home-link" className="link-line hover:text-ink transition-colors">{t.nav.home}</Link>
          <Link to="/formation" data-testid="nav-formation-link" className="link-line hover:text-ink transition-colors">{t.nav.formation}</Link>
          <Link to="/temoignages" data-testid="nav-testimonials-link" className="link-line hover:text-ink transition-colors">{t.nav.testimonials}</Link>
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="text-xs tracking-[0.2em] uppercase text-ink/50 hover:text-ink transition-colors border border-ink/15 px-3 py-1.5"
          >
            {lang === "fr" ? "FR / EN" : "EN / FR"}
          </button>

          {/* Desktop user actions */}
          <div className="hidden md:flex items-center gap-5">
            {user ? (
              <>
                <Link to="/espace-membre" data-testid="nav-member-link" className="flex items-center gap-2 text-sm text-ink/70 hover:text-ink transition-colors">
                  <LayoutGrid size={15} /> {t.nav.member}
                </Link>
                <Link to="/profil" data-testid="nav-profile-link" title={t.nav.profile} className="text-ink/50 hover:text-ink transition-colors">
                  <User size={17} />
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin-link" className="flex items-center gap-2 text-sm text-ember hover:text-emberlight transition-colors">
                    <Shield size={15} /> {t.nav.admin}
                  </Link>
                )}
                <button data-testid="nav-logout-btn" onClick={handleLogout} title={t.nav.logout} className="text-ink/50 hover:text-ink transition-colors">
                  <LogOut size={17} />
                </button>
              </>
            ) : (
              <Link
                to="/auth"
                data-testid="nav-login-btn"
                className="btn-glow bg-ink hover:bg-ember text-white text-sm px-5 py-2.5 transition-colors duration-300"
              >
                {t.nav.login}
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            data-testid="nav-hamburger-btn"
            onClick={() => setOpen(!open)}
            className="md:hidden text-ink/70 hover:text-ink transition-colors p-1"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.33, 1, 0.68, 1] }}
            className="md:hidden overflow-hidden bg-paper border-b border-ink/10"
          >
            <div className="py-2">
              <Link to="/" data-testid="mobile-nav-home" onClick={close} className={`${linkCls} ${pathname === "/" ? "text-ember" : ""}`}>
                <Home size={17} className="text-ember" /> {t.nav.home}
              </Link>
              <Link to="/formation" data-testid="mobile-nav-formation" onClick={close} className={`${linkCls} ${pathname === "/formation" ? "text-ember" : ""}`}>
                <BookOpen size={17} className="text-ember" /> {t.nav.formation}
              </Link>
              <Link to="/temoignages" data-testid="mobile-nav-testimonials" onClick={close} className={`${linkCls} ${pathname === "/temoignages" ? "text-ember" : ""}`}>
                <MessageSquareQuote size={17} className="text-ember" /> {t.nav.testimonials}
              </Link>
              {user && (
                <Link to="/espace-membre" data-testid="mobile-nav-member" onClick={close} className={`${linkCls} ${pathname === "/espace-membre" ? "text-ember" : ""}`}>
                  <LayoutGrid size={17} className="text-ember" /> {t.nav.member}
                </Link>
              )}
              {user && (
                <Link to="/profil" data-testid="mobile-nav-profile" onClick={close} className={`${linkCls} ${pathname === "/profil" ? "text-ember" : ""}`}>
                  <User size={17} className="text-ember" /> {t.nav.profile}
                </Link>
              )}
              {user && user.role === "admin" && (
                <Link to="/admin" data-testid="mobile-nav-admin" onClick={close} className={`${linkCls} ${pathname === "/admin" ? "text-ember" : ""}`}>
                  <Shield size={17} className="text-ember" /> {t.nav.admin}
                </Link>
              )}
              <div className="px-6 py-5">
                {user ? (
                  <button data-testid="mobile-nav-logout" onClick={handleLogout} className="flex items-center gap-2 text-sm text-ink/60 hover:text-red-500 transition-colors">
                    <LogOut size={16} /> {t.nav.logout}
                  </button>
                ) : (
                  <Link to="/auth" data-testid="mobile-nav-login" onClick={close} className="btn-glow inline-block bg-ink hover:bg-ember text-white text-sm px-6 py-3 transition-colors duration-300">
                    {t.nav.login}
                  </Link>
                )}
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
